import React, { useCallback, useContext, useState } from 'react';
import {
  analysisViewIdState,
  type BenchmarkInputId,
  benchmarkInputIsRelative,
  benchmarkInputName,
  benchmarkInputSubject,
  benchmarkInputType,
  isReportState,
  onBenchmarkInputGroupDelete,
  originalAnalysisSubjectQuery,
  type Subject,
} from 'venn-state';
import {
  type AnalysisSubject,
  analyticsService,
  assertExhaustive,
  isHistoricalPortfolio,
  navigateToManageDataPage,
  useHasFF,
  useModal,
  withSuspense,
} from 'venn-utils';
import { EllipsisTooltipSpan, ItemIcon, ItemType, StudioMenu, SubTitleShimmer, TitleShimmer } from 'venn-ui-kit';
import { useRecoilCallback, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { AllocationPanelLauncherAction, BenchmarkEditor, IndividualBenchmarkAlert, UserContext } from 'venn-components';
import { useHistory } from 'react-router-dom';
import type { CustomBenchmarkTypeEnum } from 'venn-api';
import { SingleSubjectSearchPopover } from './SingleSubjectSearchPopover';
import { InputRow } from './InputRow';
import { cn } from '@/lib/utils';

const MENU_CLASS = 'subject-menu';
const ACTION_MENU_CLASS = 'action-menu';

interface BenchmarkItemProps {
  subject: Subject;
  /** onSwap benefits from memoization, as it can otherwise cause excessive rendering of the dropdown. */
  onSwap?: (oldSubject: Subject, newSubject: AnalysisSubject) => void;
  readonly?: boolean;
  benchmarkIsRelative: boolean;
}

type Action = 'Manage Data' | 'Change Benchmark';

const getItemType = (item?: AnalysisSubject) => {
  if (!item) {
    return ItemType.Investment;
  }

  if (item.isCompositeBenchmark) {
    return ItemType.Benchmark;
  }
  if (item.portfolio) {
    return isHistoricalPortfolio(item.portfolio) ? ItemType.HistoricalPortfolio : ItemType.Portfolio;
  }

  return ItemType.Investment;
};

export const BenchmarkItem = withSuspense(
  <>
    <TitleShimmer />
    <SubTitleShimmer />
  </>,
  ({ subject, onSwap, readonly, benchmarkIsRelative }: BenchmarkItemProps) => {
    // Original subject is used as allocator changes do not effect the common benchmark
    const analysisSubject = useRecoilValue(originalAnalysisSubjectQuery(subject));
    const [isSwapOpen, openSwap, closeSwap] = useModal();
    const onSwapSubmit = useCallback(
      (newSubject: AnalysisSubject) => {
        closeSwap();
        onSwap?.(subject, newSubject);
      },
      [subject, closeSwap, onSwap],
    );
    const isReport = useRecoilValue(isReportState);

    const history = useHistory();
    const onSelect = (action: Action) => {
      switch (action) {
        case 'Manage Data':
          const sourceLocation = isReport ? 'Report Lab' : 'Studio';
          return navigateToManageDataPage(history, subject, sourceLocation, true);
        case 'Change Benchmark':
          return openSwap();
        default:
          return assertExhaustive(action);
      }
    };

    const menuOptions = [{ value: 'Change Benchmark' as const }, { value: 'Manage Data' as const }];

    return (
      <div className="text-[1rem]">
        <SubjectName>
          <div className="flex items-end gap-x-[4px] leading-[1]">
            <ItemIcon
              item={getItemType(analysisSubject)}
              dataSource={analysisSubject?.superItem.dataSource || analysisSubject?.portfolio?.dataSource}
              isUploaded={analysisSubject?.userUploaded && !analysisSubject?.isCompositeBenchmark}
              investmentSource={analysisSubject?.fund?.investmentSource}
            />
            <EllipsisTooltipSpan flex maxWidth={200}>
              {analysisSubject?.name}
            </EllipsisTooltipSpan>
          </div>
          {!readonly && (
            <div className={cn(ACTION_MENU_CLASS, 'flex items-end gap-x-[4px] leading-[1]')}>
              {analysisSubject && <AllocationPanelLauncherAction subject={analysisSubject} />}
              <StudioMenu label="Benchmark Options" size="tiny" onSelect={onSelect} options={menuOptions} />
            </div>
          )}
        </SubjectName>
        <RelativeToBenchmarkMessage benchmarkIsRelative={benchmarkIsRelative} />
        {isSwapOpen && (
          <SingleSubjectSearchPopover
            onClose={closeSwap}
            onSubjectSelected={onSwapSubmit}
            currentLocationForAnalytics="SubjectGroupRow"
          />
        )}
      </div>
    );
  },
);

export const RelativeToBenchmarkMessage = ({ benchmarkIsRelative }: { benchmarkIsRelative: boolean }) => {
  const hasBlockAssignment = useHasFF('block_assignment_ff');
  return (
    <div
      className={cn('', {
        'pl-[20px] h-[15px]': !hasBlockAssignment,
        'text-venn-grey-80': hasBlockAssignment,
      })}
    >
      Relative to Benchmark: {benchmarkIsRelative ? 'Yes' : 'No'}
    </div>
  );
};

export const IndividualBenchmarkItem = ({ benchmarkIsRelative }: { benchmarkIsRelative: boolean }) => {
  return (
    <div className="text-[1rem]">
      <SubjectName>
        <div>
          <ItemIcon item={ItemType.Benchmark} dataSource={undefined} isUploaded={false} investmentSource={undefined} />
          <span style={{ paddingLeft: 7 }}>Individual benchmarks</span>
        </div>
      </SubjectName>
      <RelativeToBenchmarkMessage benchmarkIsRelative={benchmarkIsRelative} />
    </div>
  );
};

// TODO: Remove this component when has_block_assignment_ff is removed
export const EmptyBenchmarkItem = ({
  benchmarkType,
  benchmarkIsRelative,
}: {
  benchmarkType: CustomBenchmarkTypeEnum;
  benchmarkIsRelative: boolean;
}) => {
  return (
    <div className="text-[1rem]">
      <SubjectName>
        <div>
          <ItemIcon item={ItemType.Benchmark} dataSource={undefined} isUploaded={false} investmentSource={undefined} />
          <span style={{ paddingLeft: 7 }}>
            {benchmarkType === 'COMMON' || benchmarkType === 'NONE' ? 'No benchmark selected' : 'Individual benchmarks'}
          </span>
        </div>
      </SubjectName>
      <RelativeToBenchmarkMessage benchmarkIsRelative={benchmarkIsRelative} />
    </div>
  );
};

interface BenchmarkProps {
  benchmarkId: BenchmarkInputId;
  deletable: boolean;
}

const Benchmark = ({ benchmarkId, deletable }: BenchmarkProps) => {
  const { hasPermission } = useContext(UserContext);
  const hasBlockAssignment = useHasFF('block_assignment_ff');

  const subject = useRecoilValue(benchmarkInputSubject(benchmarkId));
  const setBenchmarkSubject = useSetRecoilState(benchmarkInputSubject(benchmarkId));
  const benchmarkType = useRecoilValue(benchmarkInputType(benchmarkId));
  const benchmarkIsRelative = useRecoilValue(benchmarkInputIsRelative(benchmarkId));
  const [editorOpen, setEditorOpen] = useState(false);
  const [name, setName] = useRecoilState(benchmarkInputName(benchmarkId));
  const readonly = !hasPermission('STUDIO_EDIT');
  const isReport = useRecoilValue(isReportState);
  const viewId = useRecoilValue(analysisViewIdState);

  const onGroupDelete = useRecoilCallback(onBenchmarkInputGroupDelete);
  const hasIndividualBenchmarkVisbilityFeature = useHasFF('individual_benchmarks_visibility_ff');

  // onSwapSubject memoization helps prevent excessive rerendering of the swap modal and dropdown menu
  const onSwapSubject = useRecoilCallback(
    ({ snapshot }) =>
      (_oldSubject: Subject, newAnalysisSubject: AnalysisSubject) => {
        const newSubject = {
          fundId: newAnalysisSubject.fund?.id,
          portfolioId: newAnalysisSubject.portfolio?.id,
        };

        snapshot.getPromise(originalAnalysisSubjectQuery(subject)).then((analysisSubject) => {
          analyticsService.inputsBenchmarkGroupModified({
            name,
            benchmark:
              benchmarkType === 'COMMON' && analysisSubject
                ? { id: analysisSubject.id, name: analysisSubject.name }
                : undefined,
            type: benchmarkType,
            relative: benchmarkIsRelative,
            sourcePage: isReport ? 'REPORT_LAB' : 'STUDIO',
            viewId,
          });
        });

        setBenchmarkSubject(newSubject);
      },
    [setBenchmarkSubject, name, isReport, viewId, benchmarkType, subject, benchmarkIsRelative],
  );

  const onDeleteBenchmarkGroup = useRecoilCallback(
    ({ snapshot }) =>
      () => {
        snapshot.getPromise(originalAnalysisSubjectQuery(subject)).then((analysisSubject) => {
          analyticsService.inputsBenchmarkGroupDeleted({
            name,
            sourcePage: isReport ? 'REPORT_LAB' : 'STUDIO',
            benchmark:
              benchmarkType === 'COMMON' && analysisSubject
                ? {
                    id: analysisSubject.id,
                    name: analysisSubject.name,
                  }
                : undefined,
            type: benchmarkType,
            relative: benchmarkIsRelative,
            viewId,
          });
        });

        onGroupDelete(benchmarkId);
      },
    [benchmarkId, onGroupDelete, isReport, viewId, name, subject, benchmarkType, benchmarkIsRelative],
  );

  return (
    <>
      {editorOpen && <BenchmarkEditor benchmarkId={benchmarkId} onClose={() => setEditorOpen(false)} />}
      <InputRow
        testid={`qa-benchmark-input-row-${name}`}
        name={name}
        onNameChange={setName}
        onEdit={() => setEditorOpen(true)}
        onDelete={deletable ? onDeleteBenchmarkGroup : undefined}
        readonly={readonly}
        className={cn({
          'flex flex-col gap-[2px]': hasBlockAssignment,
        })}
      >
        {hasBlockAssignment ? (
          <div className="relative" data-testid="qa-benchmark-input">
            {subject && benchmarkType === 'COMMON' ? (
              <BenchmarkItem
                subject={subject}
                onSwap={onSwapSubject}
                readonly={readonly}
                benchmarkIsRelative={benchmarkIsRelative}
              />
            ) : benchmarkType === 'INDIVIDUAL' ? (
              <div className="flex flex-col gap-[4px]">
                <IndividualBenchmarkItem benchmarkIsRelative={benchmarkIsRelative} />
                {hasIndividualBenchmarkVisbilityFeature && <IndividualBenchmarkAlert />}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="relative" data-testid="qa-benchmark-input">
            {subject && benchmarkType === 'COMMON' ? (
              <BenchmarkItem
                subject={subject}
                onSwap={onSwapSubject}
                readonly={readonly}
                benchmarkIsRelative={benchmarkIsRelative}
              />
            ) : (
              <div className="flex flex-col gap-[4px]">
                <EmptyBenchmarkItem benchmarkType={benchmarkType} benchmarkIsRelative={benchmarkIsRelative} />
                {benchmarkType === 'INDIVIDUAL' && hasIndividualBenchmarkVisbilityFeature && (
                  <IndividualBenchmarkAlert />
                )}
              </div>
            )}
          </div>
        )}
      </InputRow>
    </>
  );
};

export default Benchmark;

const SubjectName = ({ children, className }: React.BaseHTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex items-center justify-between text-[14px] h-[30px] flex-1 w-full',
      `[.${MENU_CLASS}]:left-[unset] -[.${MENU_CLASS}]:right-full`,
      className,
    )}
  >
    {children}
  </div>
);
